import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  FaQuestionCircle, 
  FaStore, 
  FaUserTie, 
  FaCheckCircle, 
  FaDollarSign, 
  FaTruck,
  FaChevronDown,
  FaChevronUp,
  FaEnvelope
} from 'react-icons/fa';

const HelpCenter = () => {
  const [openSection, setOpenSection] = useState(null);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    // TODO: Implement contact form submission
    toast.success('Thank you for contacting us! We will get back to you soon.');
    setContactForm({ name: '', email: '', subject: '', message: '' });
  };

  const faqSections = [
    {
      id: 'becoming-reseller',
      title: 'Becoming a Reseller',
      icon: <FaUserTie className="text-purple-600" />,
      questions: [
        {
          q: 'How do I become a reseller?',
          a: 'To become a reseller, visit the "Become a Reseller" page (available in the footer) and fill out the application form. You\'ll need to provide business information, contact details, address, bank details, and tax information (GST, PAN). Submit the required documents and wait for admin approval.'
        },
        {
          q: 'What documents do I need?',
          a: 'You need to provide GST certificate, PAN card, bank statement, business license (if applicable), and identity proof. All documents should be clear and valid.'
        },
        {
          q: 'How long does approval take?',
          a: 'Typically, reseller applications are reviewed within 2-3 business days. You\'ll receive an email notification once your application is approved or if additional information is needed.'
        },
        {
          q: 'What happens after approval?',
          a: 'Once approved, you\'ll receive access to your reseller dashboard where you can add products, manage inventory, view orders, track earnings, and request payouts.'
        },
        {
          q: 'Can I sell any type of product?',
          a: 'You can sell wellness, fitness, and health-related products. All products must comply with our marketplace policies and will be reviewed by our admin team before being listed.'
        }
      ]
    },
    {
      id: 'adding-products',
      title: 'Adding Products',
      icon: <FaStore className="text-blue-600" />,
      questions: [
        {
          q: 'How do I add a product?',
          a: 'Go to your Reseller Dashboard and click on "My Products". Then click "Add Product" and fill in the product details including title, description, price, stock quantity, category, and images. Submit the form and wait for admin approval.'
        },
        {
          q: 'What information is required?',
          a: 'You need to provide: product title, description, price, discount (if any), stock quantity, category, product images, SKU (optional), shipping details, and return policy.'
        },
        {
          q: 'How long does product approval take?',
          a: 'Product approvals are typically processed within 1-2 business days. You\'ll be notified via email once your product is approved or if changes are needed.'
        },
        {
          q: 'Why was my product rejected?',
          a: 'Products may be rejected if they don\'t meet quality standards, violate marketplace policies, have incomplete information, or contain prohibited items. You\'ll receive a reason for rejection and can make corrections and resubmit.'
        },
        {
          q: 'Can I edit my products after approval?',
          a: 'Yes, you can edit your products anytime. However, significant changes (like price, title, or description) may require re-approval before the changes go live.'
        },
        {
          q: 'How do I manage inventory?',
          a: 'You can update stock quantities in the "My Products" section. Set up low stock alerts to get notified when inventory is running low.'
        }
      ]
    },
    {
      id: 'approval-process',
      title: 'Approval Process',
      icon: <FaCheckCircle className="text-green-600" />,
      questions: [
        {
          q: 'How does the approval process work?',
          a: 'After you submit your reseller application or add a product, our admin team reviews it for compliance with marketplace policies, quality standards, and completeness. You\'ll receive email notifications at each stage.'
        },
        {
          q: 'What are the approval statuses?',
          a: 'Pending: Under review. Approved: Accepted and live. Rejected: Not approved (with reason provided). You can check status in your dashboard.'
        },
        {
          q: 'What can I do while waiting for approval?',
          a: 'While waiting, you can prepare additional products, set up your business profile, and familiarize yourself with the reseller dashboard. You\'ll be notified as soon as your application is reviewed.'
        },
        {
          q: 'Can I appeal a rejection?',
          a: 'Yes, if your application or product is rejected, you can review the feedback, make necessary corrections, and resubmit. Contact support if you need clarification on rejection reasons.'
        }
      ]
    },
    {
      id: 'payments-payouts',
      title: 'Payments & Payouts',
      icon: <FaDollarSign className="text-yellow-600" />,
      questions: [
        {
          q: 'How do I get paid?',
          a: 'When customers purchase your products, the payment is processed. After the order is confirmed and delivered, your earnings (minus platform commission) become available for payout. You can request payouts from your Earnings dashboard.'
        },
        {
          q: 'What is the commission rate?',
          a: 'The platform commission rate varies by reseller and is set during the approval process. You can view your specific rate in your reseller dashboard.'
        },
        {
          q: 'How often can I request payouts?',
          a: 'You can request payouts anytime you have available earnings. Payouts are typically processed within 3-5 business days after your request is approved by admin.'
        },
        {
          q: 'What payment methods are supported?',
          a: 'Payouts are processed via bank transfer to the account details you provided during registration. Make sure your bank details are accurate and up-to-date.'
        },
        {
          q: 'How do I track my earnings?',
          a: 'Visit your Reseller Dashboard and go to the "Earnings" section. You can view total revenue, pending payouts, payout history, and commission breakdowns.'
        }
      ]
    },
    {
      id: 'shipping-orders',
      title: 'Shipping & Orders',
      icon: <FaTruck className="text-indigo-600" />,
      questions: [
        {
          q: 'How do I fulfill orders?',
          a: 'When you receive an order, you\'ll be notified via email and in your dashboard. Go to "My Orders", view order details, prepare the shipment, and update the order status with tracking information.'
        },
        {
          q: 'Who handles shipping?',
          a: 'As a reseller, you are responsible for shipping your products. You can use any shipping provider of your choice. Update the tracking number in the order details once shipped.'
        },
        {
          q: 'What are the shipping requirements?',
          a: 'Products should be shipped within 2-3 business days of order confirmation. Use proper packaging and provide accurate tracking information. Delays should be communicated to customers.'
        },
        {
          q: 'What if a customer wants to return a product?',
          a: 'Handle returns according to your return policy (typically 30 days). Process refunds through the platform, and update the order status accordingly.'
        },
        {
          q: 'How do I handle order cancellations?',
          a: 'If an order needs to be cancelled, update the order status and the customer will be refunded. Contact support if you need assistance with cancellations.'
        }
      ]
    }
  ];

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-b from-white to-gray-100">
      <main className="flex-1 container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <FaQuestionCircle className="text-6xl text-purple-600" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-600">
            Help Center
          </h1>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Find answers to common questions about our marketplace, becoming a reseller, and managing your business
          </p>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <NavLink
            to="/reseller-application"
            className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-shadow border-2 border-purple-200 hover:border-purple-400"
          >
            <div className="flex items-center mb-3">
              <FaUserTie className="text-3xl text-purple-600 mr-3" />
              <h3 className="text-xl font-bold text-gray-800">Become a Reseller</h3>
            </div>
            <p className="text-gray-600">Start selling on our platform</p>
          </NavLink>
          
          <NavLink
            to="/Market"
            className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-shadow border-2 border-blue-200 hover:border-blue-400"
          >
            <div className="flex items-center mb-3">
              <FaStore className="text-3xl text-blue-600 mr-3" />
              <h3 className="text-xl font-bold text-gray-800">Browse Marketplace</h3>
            </div>
            <p className="text-gray-600">Shop wellness products</p>
          </NavLink>
          
          <NavLink
            to="/reseller/dashboard"
            className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-shadow border-2 border-green-200 hover:border-green-400"
          >
            <div className="flex items-center mb-3">
              <FaCheckCircle className="text-3xl text-green-600 mr-3" />
              <h3 className="text-xl font-bold text-gray-800">Reseller Dashboard</h3>
            </div>
            <p className="text-gray-600">Manage your business</p>
          </NavLink>
        </div>

        {/* FAQ Sections */}
        <div className="space-y-6 mb-12">
          {faqSections.map((section) => (
            <div key={section.id} className="bg-white rounded-xl shadow-md overflow-hidden">
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center">
                  <div className="mr-4">{section.icon}</div>
                  <h2 className="text-xl font-bold text-gray-800">{section.title}</h2>
                </div>
                {openSection === section.id ? (
                  <FaChevronUp className="text-gray-500" />
                ) : (
                  <FaChevronDown className="text-gray-500" />
                )}
              </button>
              
              {openSection === section.id && (
                <div className="px-6 py-4 border-t border-gray-200">
                  <div className="space-y-6">
                    {section.questions.map((item, index) => (
                      <div key={index} className="pb-4 border-b border-gray-100 last:border-b-0">
                        <h3 className="font-semibold text-gray-800 mb-2">{item.q}</h3>
                        <p className="text-gray-600 leading-relaxed">{item.a}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Contact Support */}
        <div className="bg-white rounded-xl shadow-md p-8">
          <div className="flex items-center mb-6">
            <FaEnvelope className="text-3xl text-purple-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-800">Still Need Help?</h2>
          </div>
          <p className="text-gray-600 mb-6">
            Can't find what you're looking for? Contact our support team and we'll get back to you as soon as possible.
          </p>
          <form onSubmit={handleContactSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  required
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  required
                />
              </div>
            </div>
            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                Subject
              </label>
              <input
                type="text"
                id="subject"
                value={contactForm.subject}
                onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                required
              />
            </div>
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                Message
              </label>
              <textarea
                id="message"
                rows="5"
                value={contactForm.message}
                onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                required
              />
            </div>
            <button
              type="submit"
              className="w-full md:w-auto px-8 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors"
            >
              Send Message
            </button>
          </form>
        </div>
      </main>
    </div>
  );
};

export default HelpCenter;

